import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Webhook } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import { useRegisterWebhook } from '@/shared/hooks/useIntegrations';
import type { IntegrationConnection } from '@momentum/types';

const isDev = import.meta.env.VITE_ENVIRONMENT === 'development';

interface WebhookButtonProps {
  integration: IntegrationConnection;
  companyId: string;
}

export const WebhookButton = ({
  integration,
  companyId,
}: WebhookButtonProps) => {
  const { t } = useTranslation('goals');
  const registerWebhookMutation = useRegisterWebhook();

  if (!isDev) return null;

  const hasWebhook = (integration.webhookSubscriptions?.length ?? 0) > 0;
  const isRegistering = registerWebhookMutation.isPending;

  const handleRegisterWebhook = async () => {
    try {
      await registerWebhookMutation.mutateAsync({
        companyId,
        integrationId: integration._id,
      });
      toast.success(t('integrations.webhookRegistered'));
    } catch {
      toast.error(t('integrations.webhookFailed'));
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRegisterWebhook}
          disabled={isRegistering}
          className={
            hasWebhook
              ? 'relative border-green-200 bg-green-50 hover:bg-green-100'
              : 'relative'
          }
        >
          <Webhook
            className={`h-4 w-4 ${isRegistering ? 'animate-spin' : ''} ${
              hasWebhook ? 'text-green-600' : ''
            }`}
          />
          {hasWebhook && (
            <span className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full bg-green-500" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isRegistering
          ? t('integrations.registeringWebhook')
          : hasWebhook
          ? t('integrations.reconnectWebhook')
          : t('integrations.connectWebhook')}
      </TooltipContent>
    </Tooltip>
  );
};
